import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useId, useRef, useState, type ChangeEvent } from 'react';

import { Botao } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { Barra, Carregando } from '@/componentes/Esqueleto';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { SemPermissao } from '@/componentes/SemPermissao';
import { Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { formatarInteiro } from '@/lib/formato';
import { explicarFalha } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';
import { useSessao } from '@/modulos/sessao/consultas';

import {
  apiDaImportacao,
  codigoDaLinha,
  modeloEmCsv,
  TEXTO_DA_JA_CADASTRADA,
  TEXTO_DO_FORMATO,
  ordenarParaConferir,
  paraBase64,
  situacaoEscrita,
  TAMANHO_MAXIMO_DA_PLANILHA,
  type ArquivoEscolhido,
  type ResultadoDaImportacao,
  type Conferencia,
  type LinhaConferida,
} from './importacao';

/**
 * IMPORTAR A FROTA PELA PLANILHA — pedido do dono, 03/10/2026 (0060).
 *
 * Dois passos, e o primeiro não grava nada: escolher a planilha mostra o que
 * cada linha vai virar — entra, já cadastrada ou com erro, e por quê — e o
 * catálogo que vai nascer. Só "Importar" grava, numa transação só.
 *
 * ## Uma ação laranja por vez
 *
 * Antes da conferência, o laranja é "Escolher planilha". Depois dela, é
 * "Importar N equipamentos", e escolher outra planilha passa a contorno.
 */

const COLUNAS: readonly Coluna<LinhaConferida>[] = [
  {
    chave: 'linha',
    rotulo: 'Linha',
    alinhamento: 'numero',
    celula: (l) => <span className="numero">{formatarInteiro(l.numeroLinha)}</span>,
  },
  {
    chave: 'equipamento',
    /* 0131: a planilha não traz código. A coluna mostra o que cada linha nova
       RECEBE ao importar (pelo tipo e pela sequência), e o da já cadastrada. */
    rotulo: 'Código ao importar',
    prioridade: 'chave',
    celula: (l) => (
      <>
        <span
          className="codigo block text-tinta"
          {...(l.situacao === 'entra' ? { title: 'Criado ao importar, pelo tipo e pela sequência.' } : {})}
        >
          {codigoDaLinha(l)}
        </span>
        <TextoDaCelula texto={l.descricao ?? ''} classe="txt-apoio text-tinta-apagada" linhas={1} />
      </>
    ),
  },
  {
    chave: 'modelo',
    rotulo: 'Fabricante e modelo',
    celula: (l) => (
      <>
        <TextoDaCelula texto={[l.fabricante, l.modelo].filter(Boolean).join(' ')} classe="txt-corpo text-tinta" linhas={1} />
        <TextoDaCelula texto={[l.categoria, l.configuracao].filter(Boolean).join(' · ')} classe="txt-apoio text-tinta-apagada" linhas={1} />
      </>
    ),
  },
  {
    chave: 'identificacao',
    rotulo: 'Série e placa',
    prioridade: 'secundaria',
    celula: (l) => (
      <>
        <span className="codigo block text-tinta">{l.numeroSerie ?? '—'}</span>
        <span className="txt-linha2 block text-tinta-secundaria">
          {`${l.placa ?? 'sem placa'} · ${l.tipoMedidor === 'odometro' ? 'anda por km' : 'anda por hora'}`}
        </span>
      </>
    ),
  },
  {
    chave: 'situacao',
    rotulo: 'Situação',
    /* Rótulo escrito, nunca só a cor (p.09). O erro diz o que corrigir. */
    celula: (l) => (
      <div className="min-w-0">
        <span className={l.situacao === 'erro' ? 'txt-corpo text-parada' : 'txt-corpo text-tinta'}>
          {situacaoEscrita(l.situacao)}
        </span>
        {l.erros.length > 0 ? (
          <span className="txt-linha2 block text-tinta-secundaria">{l.erros.join(' ')}</span>
        ) : l.situacao === 'ja_cadastrada' ? (
          <span className="txt-linha2 block text-tinta-secundaria">{TEXTO_DA_JA_CADASTRADA}</span>
        ) : null}
      </div>
    ),
  },
];

export function PaginaImportacao() {
  const base = useId();
  const seletor = useRef<HTMLInputElement>(null);
  const navegar = useNavigate();
  const cliente = useQueryClient();
  const [arquivo, setArquivo] = useState<ArquivoEscolhido | null>(null);
  const [conferencia, setConferencia] = useState<Conferencia | null>(null);
  const [recusa, setRecusa] = useState<{ readonly erro: string; readonly comoResolver: string } | null>(null);
  const sessao = useSessao();

  /* Conferir não grava nada: a tela inteira é o retorno dele (p.43). */
  const conferir = useMutation({ mutationFn: apiDaImportacao.conferir, meta: { silencioso: true } });
  const importar = useMutation({
    meta: {
      retorno: retornoDe<ResultadoDaImportacao, ArquivoEscolhido>((r, v) => {
        const criados = r.criados.fabricantes + r.criados.modelos + r.criados.configuracoes;
        return {
          titulo: `${formatarInteiro(r.importadas)} ${r.importadas === 1 ? 'máquina importada' : 'máquinas importadas'}`,
          tom: r.comErro > 0 ? 'ressalva' : 'sucesso',
          alvo: { nome: v.nomeArquivo },
          fatos: [
            r.jaCadastradas > 0 ? `${formatarInteiro(r.jaCadastradas)} já estavam cadastradas` : null,
            r.comErro > 0 ? `${formatarInteiro(r.comErro)} ${r.comErro === 1 ? 'linha ficou' : 'linhas ficaram'} de fora` : null,
          ],
          efeito:
            criados > 0
              ? `Entraram no catálogo ${formatarInteiro(r.criados.fabricantes)} fabricantes, ${formatarInteiro(r.criados.modelos)} modelos e ${formatarInteiro(r.criados.configuracoes)} configurações`
              : 'Já estão na lista da frota',
        };
      }),
    },
    mutationFn: apiDaImportacao.importar,
    onSuccess: () => {
      void cliente.invalidateQueries();
      void navegar({ to: '/equipamentos' });
    },
  });

  async function escolher(evento: ChangeEvent<HTMLInputElement>) {
    const escolhido = evento.target.files?.[0];
    evento.target.value = '';
    if (!escolhido) return;
    setRecusa(null);
    setConferencia(null);
    if (escolhido.size > TAMANHO_MAXIMO_DA_PLANILHA) {
      setRecusa({ erro: 'a planilha é grande demais', comoResolver: 'O limite é 5 MB. Divida a frota em mais de um arquivo.' });
      return;
    }
    const lido: ArquivoEscolhido = { nomeArquivo: escolhido.name, conteudo: paraBase64(await escolhido.arrayBuffer()) };
    setArquivo(lido);
    conferir.mutate(lido, {
      onSuccess: setConferencia,
      onError: (falha) => {
        setRecusa(explicarFalha(falha));
      },
    });
  }

  function baixarModelo() {
    const url = URL.createObjectURL(new Blob([modeloEmCsv()], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'modelo-cadastro-de-equipamentos.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  const podeImportar = conferencia !== null && conferencia.entram > 0 && arquivo !== null;

  /* A tela INTEIRA é a ação (prova do marco, 07/10/2026): sem
     `equipamento.gerir`, o perfil a pedir no lugar da conferência. */
  if (sessao.data && !sessao.data.permissoes.includes('equipamento.gerir')) {
    return (
      <div className="min-w-0 flex-1 overflow-auto">
        <div className="pagina">
          <CabecalhoPagina
            sobretitulo="Equipamentos"
            titulo="Importar frota pela planilha"
            acoes={
              <Link to="/equipamentos" className="botao botao-secundario">
                Voltar à frota
              </Link>
            }
          />
          <SemPermissao modulo="a importação da frota" perfil="Administrador" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-w-0 flex-1 overflow-auto">
      <div className="pagina">
        <CabecalhoPagina
          sobretitulo="Equipamentos"
          titulo="Importar frota pela planilha"
          subtitulo="A planilha passa pela conferência antes de qualquer máquina entrar"
          acoes={
            <>
              <Link to="/equipamentos" className="botao botao-secundario">
                Voltar à frota
              </Link>
              <Botao nivel="secundario" onClick={baixarModelo}>
                Baixar modelo
              </Botao>
              <Botao
                nivel={podeImportar ? 'secundario' : 'primario'}
                disabled={conferir.isPending || importar.isPending}
                onClick={() => seletor.current?.click()}
              >
                {conferir.isPending ? 'Conferindo…' : conferencia ? 'Escolher outra planilha' : 'Escolher planilha'}
              </Botao>
              {podeImportar ? (
                <Botao
                  nivel="primario"
                  disabled={importar.isPending}
                  onClick={() => {
                    importar.mutate(arquivo, {
                      onError: (falha) => {
                        setRecusa(explicarFalha(falha));
                      },
                    });
                  }}
                >
                  {importar.isPending
                    ? 'Importando…'
                    : `Importar ${formatarInteiro(conferencia.entram)} ${conferencia.entram === 1 ? 'equipamento' : 'equipamentos'}`}
                </Botao>
              ) : null}
              <input
                ref={seletor}
                id={`${base}-planilha`}
                type="file"
                accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                aria-label="Planilha de cadastro de equipamentos"
                onChange={(e) => {
                  void escolher(e);
                }}
              />
            </>
          }
        />

        <section className="cartao flex flex-col gap-2 p-6">
          <h2 className="txt-secao text-tinta">O formato da planilha</h2>
          <p className="txt-apoio max-w-[68ch] text-pretty text-tinta-secundaria">
            Excel (.xlsx) ou CSV, uma máquina por linha, com o cabeçalho nas primeiras linhas. As colunas são achadas
            pelo nome, em qualquer ordem: patrimônio, código interno, fabricante, modelo, combustível, configuração,
            categoria, descrição, número de série, ano de fabricação, ano do modelo, ano da aquisição, placa e Renavam.
            Célula com “-” conta como vazia. {TEXTO_DO_FORMATO}
          </p>
        </section>

        {recusa === null ? null : (
          <div role="alert" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-4">
            <span className="txt-corpo text-tinta">{recusa.erro.charAt(0).toUpperCase() + recusa.erro.slice(1)}.</span>
            <span className="txt-apoio text-tinta-secundaria">{recusa.comoResolver}</span>
          </div>
        )}

        {conferir.isPending ? (
          /* A planilha esta sendo lida (p.32): o lugar da conferencia vira a
             forma dela — o resumo com o nome do arquivo, que ja se sabe, e a
             tabela com o cabecalho certo. */
          <>
            <section className="cartao flex flex-col gap-2 p-6">
              <h2 className="txt-secao text-tinta">{arquivo?.nomeArquivo ?? 'A planilha'}</h2>
              <Carregando oQue="Conferindo a planilha" className="flex flex-col gap-2">
                <Barra className="my-1 h-3 w-64 max-w-full" />
                <Barra className="my-1 h-3 w-4/5" />
              </Carregando>
            </section>
            <Tabela
              rotulo="Linhas da planilha"
              colunas={COLUNAS}
              linhas={[]}
              alturaDupla
              chaveDaLinha={(l) => String(l.numeroLinha)}
              carregando
              vazio={null}
            />
          </>
        ) : null}

        {conferencia === null ? null : (
          <section className="cartao flex flex-col gap-2 p-6">
            <h2 className="txt-secao text-tinta">{arquivo?.nomeArquivo ?? 'A planilha'}</h2>
            <p className="txt-corpo text-tinta">
              {`${formatarInteiro(conferencia.entram)} ${conferencia.entram === 1 ? 'entra' : 'entram'}`}
              {` · ${formatarInteiro(conferencia.jaCadastradas)} ${conferencia.jaCadastradas === 1 ? 'já cadastrada' : 'já cadastradas'}`}
              {` · ${formatarInteiro(conferencia.comErro)} com erro`}
            </p>
            {conferencia.novos.fabricantes.length + conferencia.novos.modelos.length + conferencia.novos.configuracoes.length === 0 ? (
              <p className="txt-apoio text-tinta-secundaria">Todo fabricante, modelo e configuração da planilha já existe no catálogo.</p>
            ) : (
              <p className="txt-apoio max-w-[68ch] text-pretty text-tinta-secundaria">
                O catálogo ganha{' '}
                {[
                  conferencia.novos.fabricantes.length > 0
                    ? `${formatarInteiro(conferencia.novos.fabricantes.length)} ${conferencia.novos.fabricantes.length === 1 ? 'fabricante' : 'fabricantes'} (${conferencia.novos.fabricantes.join(', ')})`
                    : null,
                  conferencia.novos.modelos.length > 0
                    ? `${formatarInteiro(conferencia.novos.modelos.length)} ${conferencia.novos.modelos.length === 1 ? 'modelo' : 'modelos'}`
                    : null,
                  conferencia.novos.configuracoes.length > 0
                    ? `${formatarInteiro(conferencia.novos.configuracoes.length)} ${conferencia.novos.configuracoes.length === 1 ? 'configuração' : 'configurações'}`
                    : null,
                ]
                  .filter(Boolean)
                  .join(', ')}
                . As linhas com erro ficam de fora; corrija na planilha e importe de novo.
              </p>
            )}
          </section>
        )}

        {conferencia === null ? null : (
          <Tabela
            rotulo="Linhas da planilha"
            colunas={COLUNAS}
            linhas={ordenarParaConferir(conferencia.linhas)}
            alturaDupla
            chaveDaLinha={(l) => String(l.numeroLinha)}
            vazio={
              <EstadoVazio
                titulo="A planilha não tem máquina"
                descricao="Preencha uma máquina por linha, abaixo do cabeçalho, e escolha a planilha de novo."
              />
            }
          />
        )}
      </div>
    </div>
  );
}
